import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Eye, Edit, Key, MoreVertical, Trash2, Mail } from 'lucide-react';

/**
 * AdminUserRow
 * ------------
 * One row in the User Management list on the Admin Dashboard. Owns
 * the avatar + name/email block, role badge, and the per-user
 * actions dropdown (View / Edit / Change Role / Impersonate /
 * Resend Invitation / Delete).
 *
 * Pure-presentational: all role lookups + handlers come in via props
 * so the parent stays the single source of truth for state.
 *
 * Props:
 *   user, primaryRole, roleConfig, roleName,
 *   onView, onEdit, onChangeRole, onResendInvite, onDelete,
 *   canImpersonate, onImpersonate
 */
const AdminUserRow = ({
  user,
  primaryRole,
  roleConfig,
  roleName,
  onView,
  onEdit,
  onChangeRole,
  onResendInvite,
  onDelete,
  canImpersonate,
  onImpersonate,
  isPulsing,
}) => {
  const RoleIcon = roleConfig.icon;
  // Surface a subtle "Pending invite" badge so admins can spot which
  // teammates haven't completed first-login rotation yet — that's
  // the cohort the resend action exists for.
  const pendingInvite = !!user.must_change_password;
  return (
    <div
      className={`flex items-center justify-between p-4 bg-white border rounded-lg hover:bg-gray-50 transition-all duration-500 ${
        isPulsing
          ? 'ring-2 ring-amber-400 ring-offset-2 shadow-lg animate-pulse bg-amber-50/40'
          : ''
      }`}
      data-testid={`admin-user-row-${user.user_id}`}
      data-pulsing={isPulsing ? 'true' : undefined}
    >
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleConfig.bg}`}>
          <RoleIcon size={20} className={roleConfig.text} />
        </div>
        <div>
          <p className="font-medium text-gray-900">{user.name || user.email}</p>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {pendingInvite && (
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 text-amber-700"
            data-testid={`pending-invite-${user.user_id}`}
          >
            <Mail size={12} className="mr-1" />
            Pending invite
          </Badge>
        )}
        <Badge className={`${roleConfig.bg} ${roleConfig.text}`}>
          {roleName || primaryRole}
        </Badge>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView(user)} data-testid={`view-user-${user.user_id}`}>
              <Eye size={14} className="mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(user)} data-testid={`edit-user-${user.user_id}`}>
              <Edit size={14} className="mr-2" />
              Edit User
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onChangeRole(user)}
              data-testid={`change-role-${user.user_id}`}
            >
              <Key size={14} className="mr-2" />
              Change Role
            </DropdownMenuItem>
            {onResendInvite && (
              <DropdownMenuItem
                onClick={() => onResendInvite(user)}
                data-testid={`resend-invite-${user.user_id}`}
                className="text-teal-700"
              >
                <Mail size={14} className="mr-2" />
                Resend invitation
              </DropdownMenuItem>
            )}
            {canImpersonate && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onImpersonate(user)}
                  data-testid={`impersonate-${user.user_id}`}
                  className="text-amber-600"
                >
                  <Eye size={14} className="mr-2" />
                  Impersonate
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-rose-600"
              onClick={() => onDelete(user)}
              data-testid={`delete-user-${user.user_id}`}
            >
              <Trash2 size={14} className="mr-2" />
              Delete User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default AdminUserRow;
